import { StateCreator } from "zustand";
import { EntityData } from "./types";
import { produce } from "immer";

export interface EntitySliceState<T extends EntityData> {
  entities: Record<string, T>;
  addEntity: (entity: T) => void;
  updateEntity: (id: string, updates: Partial<T>) => void;
  removeEntity: (id: string) => void;
  getEntity: (id: string) => T | undefined;
  getEntities: (ids: string[]) => (T | undefined)[];
}

export const createEntitySlice =
  <T extends EntityData, U>() =>
  (
    sliceName: keyof U & string
  ): StateCreator<U, [], [["zustand/immer", never]], EntitySliceState<T>> =>
  (set, get) => ({
    entities: {} as Record<string, T>,

    addEntity: (entity) =>
      set((state) => {
        return produce(state, (draft: U) => {
          const slice = draft[sliceName] as EntitySliceState<T>;
          const existing = slice.entities[entity.id];
          if (existing) {
            const updates = { ...entity };
            Object.assign(slice.entities[entity.id], updates);
          } else {
            slice.entities[entity.id] = entity;
          }
        });
      }),

    updateEntity: (id, updates) =>
      set((state) => {
        return produce(state, (draft: U) => {
          const slice = draft[sliceName] as EntitySliceState<T>;
          if (slice.entities[id]) {
            Object.assign(slice.entities[id], updates);
          }
        });
      }),

    removeEntity: (id) =>
      set((state) => {
        return produce(state, (draft: U) => {
          const slice = draft[sliceName] as EntitySliceState<T>;
          delete slice.entities[id];
        });
      }),

    getEntity: (id) => (get()[sliceName] as EntitySliceState<T>).entities[id],

    getEntities: (ids) =>
      ids.map((id) => (get()[sliceName] as EntitySliceState<T>).entities[id])
  });
